import StatsInfo from "@components/new-ui/IndustriesSection/StatsInfo";
import NewLayout from "@components/new-ui/Layout";
import React from "react";
import { CasestudyCard, CasestudyCardProps } from ".";
import { CaseStudyHerosection, CaseStudyInformation, FeatureList, Navbar, navLinks, OrganizationProfile } from "./alrannan-administrative-services";

const casestudyCardsDetails: CasestudyCardProps[] = [
    {
        imageUrl: "https://assets.gallabox.com/gb-home/case-study/alrannan-admin-service.png",
        title: "How Alrannan Administrative Services automated sales with Gallabox",
        sector: "Service",
        externalLink: "/case-study/alrannan-administrative-services",
    },
    {
        imageUrl: "https://assets.gallabox.com/gb-home/case-study/goireland.png",
        title: "Success Story : Improved Transparency and Increased Conversions",
        sector: "Education",
        externalLink: "/case-study/goireland",
    },
    {
        imageUrl: "https://assets.gallabox.com/gb-home/case-study/dubai-housekeeping.png",
        title: "Gallabox transforms Dubai Housekeeping's journey to service excellence on WhatsApp",
        sector: "Service",
        externalLink: "/case-study/dubai-housekeeping",
    },
];

const OdysseElectricVehicles: React.FC = () => {
    return (
        <NewLayout
            title="Odysse EV | WhatsApp API Customer Story"
            description="Discover how Odysse Electric Vehicles streamlined communication and reduced marketing spend by 80% with Gallabox's WhatsApp automations."
            canonical="https://gallabox.com/case-study/odysse-electric-vehicles"
            ogAttributes={{
                image: "https://assets.gallabox.com/gb-home/case-study/odysse/odysse-og.png",
            }}
        >
            <CaseStudyHerosection
                title="How Odysse Electric Vehicles charged up 70% business growth with Gallabox"
                description="Discover how Odysse Electric Vehicles streamlined communication and reduced marketing spend by 80% with Gallabox's WhatsApp automations."
                imageUrl={"https://assets.gallabox.com/gb-home/case-study/odysse/odysse-hero.png"}
                redirectUrl={"/case-study"}
            />
            <StatsInfo
                statsInfo={[
                    { percentage: "40%", label: "increase in lead generation" },
                    { percentage: "80%", label: "reduction in marketing spend" },
                    { percentage: "45%", label: " growth in customer acquisition" },
                ]}
            />

            <section className="max-w-6xl mx-auto  md:grid md:grid-cols-6 mt-24 mb-24">
                <div className="bg-white col-span-1">
                    <div style={{ position: "sticky", top: "100px" }}>
                        <OrganizationProfile
                            companyProfileData={[{ label: "Category", value: ["Automobile"] }]}
                            product={{
                                title: "Features Used",
                                productLink: [
                                    { link: "https://gallabox.com/whatsapp-chatbot", label: "Whatsapp Chatbot" },
                                    { link: "https://gallabox.com/whatsapp-broadcast", label: "Broadcast" },
                                    { link: "https://gallabox.com/whatsapp-integrations", label: "Integrations" },
                                    { link: "https://gallabox.com/whatsapp-shared-inbox", label: "Shared team Inbox" },
                                ],
                            }}
                        />
                    </div>
                </div>
                <div className=" col-span-4 md:pl-8 px-4">
                    <div className="flex flex-col gap-y-12">
                        <section id="About">
                            <p>
                                Born in 2020 from the Vora Group&apos;s three-decade legacy in alternative fuels,
                                <a href="https://odysse.in/" className="text-blue-500 px-1">
                                    Odysse
                                </a>
                                Electric Vehicles represents India&apos;s bold answer to urban mobility challenges.
                            </p>
                            <p className="mt-5">
                                With a mission to drive the world&apos;s transition to electric vehicles, Odysse has rapidly expanded across India, offering everything from
                                affordable e-scooters to the sporty EVOQ1S electric motorcycle that delivers 140km of emission-free range.
                            </p>
                        </section>

                        <section id="Challenges">
                            <CaseStudyInformation subTitle={"The service disconnect in the EV revolution"} className="mt-0" />
                            <p className="mt-2">
                                For a company revolutionizing how Indians commute, Odysse faced a fundamental challenge that affected their scaling plans. As Parmi Vora,
                                Odysse&apos;s Marketing Head, explains, service issues with prospects and customers were becoming a critical bottleneck. In the electric vehicle
                                industry, where consumer education and post-purchase support are crucial for adoption, poor service experiences can kill momentum.
                            </p>
                            <p className="mt-5">
                                The problem was particularly acute given Odysse&apos;s diverse product range and rapidly expanding dealer network across 45+ cities. Prospects
                                needed guidance choosing between models ranging from ₹71,100 e-scooters to ₹1.71 lakh electric motorcycles.
                            </p>
                        </section>

                        <section id="Solutions">
                            <CaseStudyInformation subTitle={"From manual management to smart messaging solutions"} className="mt-0" />
                            <p className="mt-2">
                                Parmi recalls that their manual approach to customer support was clearly unsustainable for a fast-growing EV company. While evaluating alternatives
                                like Gupshup, Odysse ultimately chose Gallabox based on peer recommendations – a decision that would transform their customer experience.
                            </p>

                            <CaseStudyInformation subTitle={"One goal: Streamline customer communication"} className="mt-12" />
                            <p className="mt-2">
                                Unlike companies with complex multi-objective implementations, Odysse had laser focus. They needed to streamline customer communication across their
                                entire journey – from initial interest through post-purchase support. This singular clarity of purpose would drive exceptional results.
                            </p>
                            <p className="mt-5">The implementation centered on two powerful features:</p>

                            <FeatureList
                                List={[
                                    {
                                        listItem:
                                            "Broadcast messaging now pushes targeted communications about new models, charging tips, and service reminders to segmented customer groups.",
                                    },
                                    {
                                        listItem:
                                            "Chatbots handle the flood of inquiries about specifications, pricing, dealer locations, and the government's FAME 2 subsidies – critical information for price-conscious Indian consumers.",
                                    },
                                ]}
                                className="mt-4 bg-gray-100"
                            />
                        </section>

                        <section id="Impact">
                            <CaseStudyInformation subTitle={"The 70% growth surge"} className="mt-0" />

                            <p className="mt-2">
                                A remarkable business growth of 70% reflects fundamental improvements in how Odysse engages with India&apos;s evolving EV market using Gallabox:
                            </p>
                            <CaseStudyInformation subTitle={"Transformation by the numbers"} className="mt-10" />
                            <FeatureList
                                List={[
                                    {
                                        listItem: "Lead generation shot up 40%: Better response times and informative chatbots convert curious browsers into serious buyers",
                                    },
                                    {
                                        listItem: "Customer acquisition rate grew 45%: Educated prospects make confident purchase decisions",
                                    },
                                    {
                                        listItem: "Marketing spend reduced by 80%: Click-to-WhatsApp ads and Broadcast prove to reduce dependence on other marketing channels",
                                    },
                                ]}
                                className="mt-4 bg-gray-100"
                            />
                            <CaseStudyInformation subTitle={"The marketing efficiency that every business dreams of"} className="mt-10" />
                            <p className="mt-2">
                                The 80% reduction in marketing spend deserves a separate story by itself. In India&apos;s competitive two-wheeler market, where established petrol
                                brands dominate mindshare, Odysse showed that exceptional customer service creates more impact than flashy campaigns.
                            </p>
                            <p className="mt-5">
                                When prospects can instantly learn about the real-world range expectations, and locate their nearest dealer, they&apos;re more likely to take the
                                electric leap. When existing customers receive proactive support and feel heard, they become the brand&apos;s most credible advocates.
                            </p>
                            <p className="mt-5">
                                As India accelerates toward its electric mobility goals, Odysse&apos;s Gallabox-powered communication infrastructure positions them perfectly for
                                the surge in demand. With plans to expand to 150 cities and new battery-swapping partnerships with Sun Mobility for international markets, managing
                                customer relationships at scale is no longer a constraint but a competitive advantage.
                            </p>
                        </section>
                    </div>
                </div>
                <div>
                    <div style={{ position: "sticky", top: "100px", minHeight: "510px" }} className="hidden md:block">
                        <Navbar links={navLinks} />
                    </div>
                </div>
            </section>
            <div className="max-w-6xl mx-auto">
                <div>
                    <h3 className="text-gray-900 max-w-6xl mx-auto mb-10 mt-5 ml-4 text-center md:text-left">Keep exploring stories like this.</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto gap-10 mb-20">
                        {casestudyCardsDetails?.map((data, index) => (
                            <CasestudyCard key={index} {...data} />
                        ))}
                    </div>
                </div>
            </div>
        </NewLayout>
    );
};

export default OdysseElectricVehicles;
